import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
	getLocationLimitsAndControls,
	getWorkerTypeHours,
	resetLocationLimitsAndControls,
	resetResetLocationLimitsAndControls,
	resetUpdateLocationLimitsAndControls,
	updateLocationLimitsAndControls,
} from "../../../../../store/gat-admin-slice";
import {
	LocationLimitsAndControls,
	WorkerTypeHours,
} from "../../../../../store/gat-admin-slice-types";
import {
	getFirstShiftLimitError,
	getShiftLimitErrors,
} from "../../../../limits-controls-bounds";
import { ShiftLimitErrors } from "../../../../limits-controls-bounds";

const useLocationLimitsControlsController = (
	locationId: number,
	companyId: number
) => {
	const dispatch = useDispatch();

	const { data: limitsAndControls, status: getStatus } = useSelector(
		(state: any) => state.gatAdminConfiguration.getLocationLimitsAndControls
	);
	const { status: updateStatus, errorMessage: updateErrorMessage } =
		useSelector(
			(state: any) =>
				state.gatAdminConfiguration.updateLocationLimitsAndControls
		);
	const { status: resetStatus, errorMessage: resetErrorMessage } = useSelector(
		(state: any) =>
			state.gatAdminConfiguration.resetLocationLimitsAndControls
	);
	const companyWorkerTypeHours: WorkerTypeHours[] = useSelector(
		(state: any) => state.gatAdminConfiguration.getWorkerTypeHours.data
	);
	const workerSubTypeEnabled = useSelector(
		(state: any) =>
			state.gatAdminConfiguration.companySettings["workerSubType.enabled"]
				?.value
	);

	const [limitControl, setLimitControl] =
		useState<LocationLimitsAndControls | null>(null);
	const [newLimitControl, setNewLimitControl] =
		useState<LocationLimitsAndControls | null>(null);
	const [validationError, setValidationError] = useState("");

	useEffect(() => {
		if (locationId) {
			dispatch(getLocationLimitsAndControls(locationId));
		}
	}, [locationId, dispatch]);

	// Company worker type hours provide the display names for worker type rows
	useEffect(() => {
		if (companyId && companyWorkerTypeHours.length === 0) {
			dispatch(getWorkerTypeHours(companyId));
		}
	}, [companyId, companyWorkerTypeHours.length, dispatch]);

	useEffect(() => {
		if (getStatus === "fulfilled" && limitsAndControls) {
			setLimitControl(limitsAndControls);
			setNewLimitControl(limitsAndControls);
		}
	}, [getStatus, limitsAndControls]);

	const getWorkerTypeName = useCallback(
		(workerTypeId: number, workerSubTypeId: number | null) => {
			const match = companyWorkerTypeHours.find(
				(wth) =>
					wth.workerTypeId === workerTypeId &&
					wth.workerSubTypeId === workerSubTypeId
			);
			if (!match) return `Worker type ${workerTypeId}`;
			return match.workerSubTypeName
				? `${match.workerTypeName} - ${match.workerSubTypeName}`
				: match.workerTypeName;
		},
		[companyWorkerTypeHours]
	);

	const handleWorkerTypeHoursChange = useCallback(
		(
			workerTypeId: number,
			workerSubTypeId: number | null,
			type: "min" | "max",
			value: number
		) => {
			const sanitizedValue = isNaN(value)
				? 0
				: Math.max(0, Math.min(value, 168));
			setNewLimitControl((prev) => {
				if (!prev) return prev;
				const field = type === "min" ? "minHours" : "maxHours";
				return {
					...prev,
					workerTypeHours: prev.workerTypeHours.map((row) => {
						if (
							row.workerTypeId === workerTypeId &&
							row.workerSubTypeId === workerSubTypeId
						) {
							return { ...row, [field]: sanitizedValue };
						}
						return row;
					}),
				};
			});
		},
		[]
	);

	const shiftLimitErrors: ShiftLimitErrors = useMemo(() => {
		if (!newLimitControl) return {};
		return getShiftLimitErrors({
			defaultDuration: newLimitControl.defaultShiftDuration,
			minDuration: newLimitControl.minShiftDuration,
			maxDuration: newLimitControl.maxShiftDuration,
			maxContinuousHours: newLimitControl.maxContiniousSignUpHours,
		});
	}, [newLimitControl]);

	const validate = useCallback(() => {
		if (!newLimitControl) return "";
		const shiftLimitError = getFirstShiftLimitError(shiftLimitErrors);
		if (shiftLimitError) return shiftLimitError;
		for (const row of newLimitControl.workerTypeHours) {
			// Max hours of 0 means unlimited
			if (row.maxHours !== 0 && row.minHours > row.maxHours) {
				return `Minimum hours (${row.minHours}) cannot exceed maximum hours (${
					row.maxHours
				}) for ${getWorkerTypeName(row.workerTypeId, row.workerSubTypeId)}.`;
			}
		}
		return "";
	}, [newLimitControl, shiftLimitErrors, getWorkerTypeName]);

	const handleSave = useCallback(() => {
		if (!newLimitControl) return;
		const error = validate();
		if (error) {
			setValidationError(error);
			return;
		}
		setValidationError("");
		// Strip in case the fetched settings still carry this retired field.
		const { restrictFlexHours, ...data } = newLimitControl as
			LocationLimitsAndControls & { restrictFlexHours?: boolean };
		dispatch(
			updateLocationLimitsAndControls({
				locationId,
				data,
			})
		);
	}, [newLimitControl, validate, locationId, dispatch]);

	const handleResetToCompany = useCallback(() => {
		dispatch(resetLocationLimitsAndControls(locationId));
	}, [locationId, dispatch]);

	// After a reset the effective values are the company's — re-fetch them
	useEffect(() => {
		if (resetStatus === "fulfilled" && locationId) {
			dispatch(getLocationLimitsAndControls(locationId));
		}
	}, [resetStatus, locationId, dispatch]);

	const closeUpdateSnackbar = useCallback(() => {
		dispatch(resetUpdateLocationLimitsAndControls());
	}, [dispatch]);

	const closeResetSnackbar = useCallback(() => {
		dispatch(resetResetLocationLimitsAndControls());
	}, [dispatch]);

	const closeValidationSnackbar = useCallback(() => {
		setValidationError("");
	}, []);

	return [
		{
			limitControl,
			newLimitControl,
			getStatus,
			updateStatus,
			updateErrorMessage,
			resetStatus,
			resetErrorMessage,
			validationError,
			shiftLimitErrors,
			workerSubTypeEnabled,
		},
		{
			setNewLimitControl,
			handleWorkerTypeHoursChange,
			getWorkerTypeName,
			handleSave,
			handleResetToCompany,
			closeUpdateSnackbar,
			closeResetSnackbar,
			closeValidationSnackbar,
		},
	] as const;
};

export default useLocationLimitsControlsController;
